import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Tag, Space, Row, Col, Card, Typography, Popconfirm, Tabs, DatePicker, Checkbox, Alert } from 'antd';
import { PlusOutlined, DownloadOutlined, DeleteOutlined, FileProtectOutlined, EditOutlined, ReloadOutlined, EyeOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || '';

export default function AgreementGenerator({ user }) {
  const role = user?.role || 'viewer';
  const canEdit = ['admin', 'manager'].includes(role);
  const authToken = localStorage.getItem('token');

  const [songs, setSongs] = useState([]);
  const [generated, setGenerated] = useState([]);
  const [singers, setSingers] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(false);

  // generate controls
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [genDate, setGenDate] = useState(dayjs());
  const [genTypes, setGenTypes] = useState(['singer_noc', 'studio_noc']);
  const [generating, setGenerating] = useState(false);
  const [songSearch, setSongSearch] = useState('');

  // Generated tab filters
  const [genFrom, setGenFrom] = useState(null);
  const [genTo, setGenTo] = useState(null);
  const [genFilterType, setGenFilterType] = useState('');
  const [genSearch, setGenSearch] = useState('');

  // master modals
  const [singerModal, setSingerModal] = useState(false);
  const [studioModal, setStudioModal] = useState(false);
  const [editingSinger, setEditingSinger] = useState(null);
  const [editingStudio, setEditingStudio] = useState(null);
  const [singerForm] = Form.useForm();
  const [studioForm] = Form.useForm();
  const [companyForm] = Form.useForm();
  const [savingCompany, setSavingCompany] = useState(false);
  const [tpl, setTpl] = useState({ singer: '', studio: '' });
  const [tplDefaults, setTplDefaults] = useState({ singer: '', studio: '' });
  const [tplTokens, setTplTokens] = useState(null);
  const [savingTpl, setSavingTpl] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g, sn, st, co, tp] = await Promise.all([
        api.get('/songs'),
        api.get('/agreement-gen/generated'),
        api.get('/agreement-gen/singers'),
        api.get('/agreement-gen/studios'),
        api.get('/agreement-gen/company'),
        api.get('/agreement-gen/templates'),
      ]);
      setSongs(Array.isArray(s.data) ? s.data : (s.data.songs || []));
      setGenerated(g.data || []);
      setSingers(sn.data || []);
      setStudios(st.data || []);
      if (co.data) companyForm.setFieldsValue(co.data);
      if (tp.data) {
        setTpl({ singer: tp.data.singer?.current || '', studio: tp.data.studio?.current || '' });
        setTplDefaults(tp.data.defaults || { singer: '', studio: '' });
        setTplTokens(tp.data.tokens || null);
      }
    } catch (e) {
      message.error(e.uiMessage || 'Load failed');
    }
    setLoading(false);
  }, [companyForm]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doGenerate = async () => {
    if (selectedSongs.length === 0) { message.warning('Pehle gaane select karo'); return; }
    if (genTypes.length === 0) { message.warning('Kam se kam ek document type chuno'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/agreement-gen/generate', {
        song_ids: selectedSongs,
        date: genDate.format('YYYY-MM-DD'),
        types: genTypes,
      });
      const d = res.data;
      message.success(d.message || 'Generated');
      if ((d.missing_singer || []).length) {
        Modal.warning({ title: 'In gaano ka Singer master data nahi mila (NOC nahi bana)', content: d.missing_singer.join(', '), width: 600 });
      }
      if ((d.missing_studio || []).length) {
        Modal.info({ title: 'In gaano ka Studio master data nahi mila (Studio NOC nahi bana)', content: d.missing_studio.join(', '), width: 600 });
      }
      setSelectedSongs([]);
      loadAll();
    } catch (e) {
      message.error(e.uiMessage || 'Generate failed');
    }
    setGenerating(false);
  };

  // ---- Singer master ----
  const openSinger = (rec) => {
    setEditingSinger(rec || null);
    singerForm.setFieldsValue(rec || { name: '', father_name: '', relation: 'S/O', address: '', pan: '', aadhaar: '', mobile: '' });
    setSingerModal(true);
  };
  const saveSinger = async () => {
    const v = await singerForm.validateFields();
    try {
      if (editingSinger) await api.put(`/agreement-gen/singers/${editingSinger.id}`, v);
      else await api.post('/agreement-gen/singers', v);
      message.success('Saved');
      setSingerModal(false);
      loadAll();
    } catch (e) { message.error(e.uiMessage || 'Save failed'); }
  };
  const delSinger = async (id) => { try { await api.delete(`/agreement-gen/singers/${id}`); loadAll(); } catch (e) { message.error(e.uiMessage); } };

  // ---- Studio master ----
  const openStudio = (rec) => {
    setEditingStudio(rec || null);
    studioForm.setFieldsValue(rec || { name: '', rep_name: '', rep_father: '', address: '', pan: '', aadhaar: '', mobile: '' });
    setStudioModal(true);
  };
  const saveStudio = async () => {
    const v = await studioForm.validateFields();
    try {
      if (editingStudio) await api.put(`/agreement-gen/studios/${editingStudio.id}`, v);
      else await api.post('/agreement-gen/studios', v);
      message.success('Saved');
      setStudioModal(false);
      loadAll();
    } catch (e) { message.error(e.uiMessage || 'Save failed'); }
  };
  const delStudio = async (id) => { try { await api.delete(`/agreement-gen/studios/${id}`); loadAll(); } catch (e) { message.error(e.uiMessage); } };

  // ---- Company / NOC details master ----
  const saveCompany = async () => {
    try {
      const v = await companyForm.validateFields();
      setSavingCompany(true);
      await api.post('/agreement-gen/company', v);
      message.success('Company / NOC details saved. Aage ke sab agreement me yahi aayega.');
    } catch (e) { if (e?.uiMessage) message.error(e.uiMessage); }
    setSavingCompany(false);
  };

  // ---- NOC full template master ----
  const saveTemplate = async () => {
    try {
      setSavingTpl(true);
      await api.post('/agreement-gen/templates', { singer: tpl.singer, studio: tpl.studio });
      message.success('NOC template save ho gaya. Aage ke sab agreement isi format/content me banenge.');
    } catch (e) { if (e?.uiMessage) message.error(e.uiMessage); }
    setSavingTpl(false);
  };
  const resetTemplate = (type) => setTpl(t => ({ ...t, [type]: tplDefaults[type] || '' }));

  const delGenerated = async (id) => { try { await api.delete(`/agreement-gen/generated/${id}`); loadAll(); } catch (e) { message.error(e.uiMessage); } };

  const [fixing, setFixing] = React.useState(false);
  const fixA4 = async () => {
    setFixing(true);
    try {
      const res = await api.post('/agreement-gen/fix-a4', {});
      const d = res.data || {};
      message.success(`${d.fixed || 0} purane NOC A4 me theek ho gaye${d.already_a4 ? ` (${d.already_a4} pehle se A4 the)` : ''}`);
      loadAll();
    } catch (e) { message.error(e.uiMessage || 'Fix failed'); }
    setFixing(false);
  };

  const filteredSongs = songs.filter(s => !songSearch || (s.title || '').toLowerCase().includes(songSearch.toLowerCase()));

  const genBySong = React.useMemo(() => {
    const m = {};
    (generated || []).forEach(g => {
      if (g.song_id == null) return;
      (m[g.song_id] = m[g.song_id] || {})[g.doc_type] = true;
    });
    return m;
  }, [generated]);
  const doneCount = songs.filter(s => genBySong[s.id]).length;

  const filteredGenerated = React.useMemo(() => {
    const from = genFrom ? genFrom.format('YYYY-MM-DD') : null;
    const to = genTo ? genTo.format('YYYY-MM-DD') : null;
    const q = (genSearch || '').toLowerCase();
    return (generated || []).filter(g => {
      if (genFilterType && g.doc_type !== genFilterType) return false;
      const d = g.agreement_date ? String(g.agreement_date).slice(0, 10) : '';
      if (from && d && d < from) return false;
      if (to && d && d > to) return false;
      if (q && !((g.song_title || '').toLowerCase().includes(q) || (g.party_name || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [generated, genFrom, genTo, genFilterType, genSearch]);

  const zipUrl = (format) => {
    const p = new URLSearchParams();
    if (genFrom) p.set('from', genFrom.format('YYYY-MM-DD'));
    if (genTo) p.set('to', genTo.format('YYYY-MM-DD'));
    if (genFilterType) p.set('type', genFilterType);
    if (genSearch) p.set('search', genSearch);
    p.set('format', format);
    p.set('token', authToken);
    return `${API_BASE}/api/agreement-gen/download-zip?${p.toString()}`;
  };

  const songColumns = [
    { title: 'Title', dataIndex: 'title', key: 'title' },
    { title: 'Singer', dataIndex: 'singer', key: 'singer', render: v => v || '-' },
    { title: 'Studio', dataIndex: 'studio', key: 'studio', render: v => v || '-' },
    {
      title: 'NOC Status', key: 'noc', render: (_, r) => {
        const set = genBySong[r.id];
        if (!set) return <Tag>Pending</Tag>;
        return (
          <Space size={4} wrap>
            {set['singer_noc'] && <Tag color="green" icon={<CheckCircleOutlined />}>Singer</Tag>}
            {set['studio_noc'] && <Tag color="volcano" icon={<CheckCircleOutlined />}>Studio</Tag>}
          </Space>
        );
      }
    },
    { title: 'Added', dataIndex: 'created_at', key: 'created_at', render: v => v ? dayjs(v).format('DD MMM YYYY') : '-' },
  ];

  const genColumns = [
    { title: 'Song', dataIndex: 'song_title', key: 'song_title' },
    { title: 'Type', dataIndex: 'doc_type', key: 'doc_type', render: v => v === 'studio_noc' ? <Tag color="volcano">Studio NOC</Tag> : <Tag color="green">Singer NOC</Tag> },
    { title: 'Party', dataIndex: 'party_name', key: 'party_name', render: v => v || '-' },
    { title: 'Date', dataIndex: 'agreement_date', key: 'agreement_date', render: v => v ? dayjs(v).format('DD MMM YYYY') : '-' },
    { title: 'Generated', dataIndex: 'created_at', key: 'created_at', render: v => v ? dayjs(v).format('DD MMM, HH:mm') : '-' },
    {
      title: 'Action', key: 'action', render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(`${API_BASE}/api/agreement-gen/view/${r.id}?token=${authToken}`, '_blank')}>View (PDF)</Button>
          <Button size="small" icon={<DownloadOutlined />} href={`${API_BASE}/api/agreement-gen/download/${r.id}?format=pdf&token=${authToken}`} target="_blank">PDF</Button>
          <Button size="small" type="primary" icon={<DownloadOutlined />} href={`${API_BASE}/api/agreement-gen/download/${r.id}?format=doc&token=${authToken}`} target="_blank">Word (edit)</Button>
          {canEdit && <Popconfirm title="Delete?" onConfirm={() => delGenerated(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}
        </Space>
      )
    },
  ];

  const tabs = [
    {
      key: 'generate', label: 'Generate Agreements', children: (
        <div>
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Gaane select karo, date chuno, aur document type — turant Singer NOC / Studio NOC Word file ban jayegi (2 singer ho to ek combined NOC). Singer/Studio ki detail 'Master Data' tab se aati hai." />
          <Alert type="success" showIcon style={{ marginBottom: 12 }}
            message={`NOC ban chuka: ${doneCount} / ${songs.length} song  •  Baaki (pending): ${songs.length - doneCount}`}
            description="Jis song ka NOC ban gaya wo green highlight + 'Singer/Studio' tick se dikhta hai. Naya song Library/Metadata me aate hi yahan aa jata hai aur uska NOC auto-ready ho jata hai." />
          <Row gutter={12} align="middle" style={{ marginBottom: 12 }}>
            <Col><Text strong>Date:</Text> <DatePicker value={genDate} onChange={(d) => setGenDate(d || dayjs())} format="DD-MM-YYYY" allowClear={false} /></Col>
            <Col>
              <Checkbox.Group value={genTypes} onChange={setGenTypes} options={[
                { label: 'Singer NOC', value: 'singer_noc' },
                { label: 'Studio NOC', value: 'studio_noc' },
              ]} />
            </Col>
            <Col flex="auto"><Input.Search placeholder="Song title search" allowClear onChange={e => setSongSearch(e.target.value)} style={{ maxWidth: 260 }} /></Col>
            <Col>
              <Button type="primary" size="large" icon={<FileProtectOutlined />} loading={generating} onClick={doGenerate} disabled={!canEdit}>
                Generate ({selectedSongs.length})
              </Button>
            </Col>
          </Row>
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={filteredSongs}
            columns={songColumns}
            rowSelection={{ selectedRowKeys: selectedSongs, onChange: setSelectedSongs, preserveSelectedRowKeys: true }}
            onRow={(r) => (genBySong[r.id] ? { style: { background: '#f6ffed' } } : {})}
            pagination={{ pageSize: 20, showSizeChanger: true }}
          />
        </div>
      )
    },
    {
      key: 'generated', label: `Generated (${generated.length})`, children: (
        <div>
          {canEdit && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message="Purane NOC bade size me ban gaye the?"
              description="Ye button dabao — sabhi purane NOC ko A4 (print) size me theek kar dega. Content, naam, date, sab waise hi rahega."
              action={<Popconfirm title="Sabhi purane NOC A4 me theek karein?" onConfirm={fixA4}><Button type="primary" loading={fixing}>Purane NOC A4 me theek karo</Button></Popconfirm>}
            />
          )}
          <Card size="small" style={{ marginBottom: 12 }}>
            <Space wrap>
              <span>Date:</span>
              <DatePicker placeholder="From" value={genFrom} onChange={setGenFrom} />
              <DatePicker placeholder="To" value={genTo} onChange={setGenTo} />
              <Select
                placeholder="Type" allowClear style={{ width: 150 }}
                value={genFilterType || undefined} onChange={v => setGenFilterType(v || '')}
                options={[{ value: 'singer_noc', label: 'Singer NOC' }, { value: 'studio_noc', label: 'Studio NOC' }]}
              />
              <Input.Search placeholder="Song / party search" allowClear style={{ width: 220 }} onChange={e => setGenSearch(e.target.value)} />
              <Button icon={<DownloadOutlined />} type="primary" href={zipUrl('doc')} target="_blank">
                Download ZIP (Word)
              </Button>
              <Button icon={<DownloadOutlined />} href={zipUrl('pdf')} target="_blank">
                Download ZIP (PDF)
              </Button>
            </Space>
            <div style={{ color: '#888', marginTop: 6, fontSize: 12 }}>
              Filter lagao (ya khali chhodo = sab). ZIP me Singer NOC aur Studio NOC alag-alag folder me aayenge.
            </div>
          </Card>
          <Table rowKey="id" size="small" loading={loading} dataSource={filteredGenerated} columns={genColumns} pagination={{ pageSize: 20 }} />
        </div>
      )
    },
    {
      key: 'master', label: 'Master Data', children: (
        <Row gutter={16}>
          <Col xs={24} style={{ marginBottom: 16 }}>
            <Card title="Company / NOC Details (Assignee)" size="small"
              extra={canEdit && <Button size="small" type="primary" loading={savingCompany} onClick={saveCompany}>Save</Button>}>
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="Ye details har Singer & Studio NOC me aati hain. Yahan ek baar edit karke Save karo — aage se jitne bhi naye agreement banenge sab me yahi updated detail aayegi (page format waisa hi rahega)." />
              <Form form={companyForm} layout="vertical" disabled={!canEdit}>
                <Row gutter={12}>
                  <Col xs={24} md={12}><Form.Item name="name" label="Company Name"><Input /></Form.Item></Col>
                  <Col xs={24} md={12}><Form.Item name="gstin" label="GSTIN"><Input /></Form.Item></Col>
                  <Col xs={24}><Form.Item name="addr" label="Company Address"><Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} /></Form.Item></Col>
                  <Col xs={24} md={8}><Form.Item name="signatory" label="Authorised Signatory"><Input /></Form.Item></Col>
                  <Col xs={24} md={8}><Form.Item name="designation" label="Designation"><Input /></Form.Item></Col>
                  <Col xs={24} md={8}><Form.Item name="place" label="Place (Date/Place line)"><Input /></Form.Item></Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="default_studio_id" label="Default Studio (jab song me studio na ho)"
                      extra="Weekly Monday email me har song ki Studio NOC banane ke liye — jis song me studio set nahi hai uske liye ye studio use hoga.">
                      <Select allowClear placeholder="Studio choose karo"
                        options={(studios || []).map(s => ({ value: s.id, label: s.name }))} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
          <Col xs={24} style={{ marginBottom: 16 }}>
            <Card title="NOC Template — Poora Agreement (Editable)" size="small"
              extra={canEdit && <Button size="small" type="primary" loading={savingTpl} onClick={saveTemplate}>Save Template</Button>}>
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message="Yahan se poora NOC content edit kar sakte ho (heading, clauses, wording — sab)."
                description={<span>
                  Jo <b>[[TOKEN]]</b> likhe hain wo automatic bhar jate hain (naam/song/date/company). Unhe waise hi rehne dena, sirf aas-paas ka text badalna.
                  Save karte hi aage ke sab agreement isi me banenge. Page ka A4 format apne aap set rehta hai — kharab nahi hoga.
                  {tplTokens && <div style={{ marginTop: 8, fontSize: 12 }}>
                    <div><b>Common:</b> {(tplTokens.common || []).join('  ')}</div>
                    <div><b>Singer:</b> {(tplTokens.singer || []).join('  ')}</div>
                    <div><b>Studio:</b> {(tplTokens.studio || []).join('  ')}</div>
                  </div>}
                </span>} />
              <Row gutter={16}>
                <Col xs={24} lg={12}>
                  <Space style={{ marginBottom: 6 }}><Text strong>Singer NOC</Text>
                    {canEdit && <Button size="small" onClick={() => resetTemplate('singer')}>Reset default</Button>}</Space>
                  <Input.TextArea value={tpl.singer} onChange={e => setTpl(t => ({ ...t, singer: e.target.value }))}
                    disabled={!canEdit} autoSize={{ minRows: 12, maxRows: 26 }}
                    style={{ fontFamily: 'monospace', fontSize: 12 }} />
                </Col>
                <Col xs={24} lg={12}>
                  <Space style={{ marginBottom: 6 }}><Text strong>Studio NOC</Text>
                    {canEdit && <Button size="small" onClick={() => resetTemplate('studio')}>Reset default</Button>}</Space>
                  <Input.TextArea value={tpl.studio} onChange={e => setTpl(t => ({ ...t, studio: e.target.value }))}
                    disabled={!canEdit} autoSize={{ minRows: 12, maxRows: 26 }}
                    style={{ fontFamily: 'monospace', fontSize: 12 }} />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Singers" size="small" extra={canEdit && <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openSinger(null)}>Add Singer</Button>}>
              <Table rowKey="id" size="small" dataSource={singers} pagination={false}
                columns={[
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Father', dataIndex: 'father_name', render: (v, r) => v ? `${r.relation || 'S/O'} ${v}` : '-' },
                  { title: 'Address', dataIndex: 'address', ellipsis: true, render: v => v || '-' },
                  canEdit && {
                    title: '', key: 'a', width: 80, render: (_, r) => (
                      <Space>
                        <Button size="small" icon={<EditOutlined />} onClick={() => openSinger(r)} />
                        <Popconfirm title="Delete?" onConfirm={() => delSinger(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                      </Space>
                    )
                  },
                ].filter(Boolean)}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Studios" size="small" extra={canEdit && <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openStudio(null)}>Add Studio</Button>}>
              <Table rowKey="id" size="small" dataSource={studios} pagination={false}
                columns={[
                  { title: 'Studio', dataIndex: 'name' },
                  { title: 'Owner', dataIndex: 'rep_name', render: v => v || '-' },
                  { title: 'Address', dataIndex: 'address', ellipsis: true, render: v => v || '-' },
                  canEdit && {
                    title: '', key: 'a', width: 80, render: (_, r) => (
                      <Space>
                        <Button size="small" icon={<EditOutlined />} onClick={() => openStudio(r)} />
                        <Popconfirm title="Delete?" onConfirm={() => delStudio(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                      </Space>
                    )
                  },
                ].filter(Boolean)}
              />
            </Card>
          </Col>
        </Row>
      )
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={3} style={{ margin: 0 }}><FileProtectOutlined /> Agreement Generator</Title></Col>
        <Col><Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button></Col>
      </Row>
      <Tabs items={tabs} />

      <Modal title={editingSinger ? 'Edit Singer' : 'Add Singer'} open={singerModal} onOk={saveSinger} onCancel={() => setSingerModal(false)} okText="Save">
        <Form form={singerForm} layout="vertical">
          <Form.Item name="name" label="Full Name (jaisa NOC me chahiye, e.g. Dharmendra Kumar (DG Mawai))" rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={8}>
            <Col span={16}><Form.Item name="father_name" label="Father's Name"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="relation" label="Relation" initialValue="S/O"><Select options={[{ value: 'S/O', label: 'S/O (son of)' }, { value: 'D/O', label: 'D/O (daughter of)' }, { value: 'W/O', label: 'W/O (wife of)' }]} /></Form.Item></Col>
          </Row>
          <Form.Item name="address" label="Full Address"><Input.TextArea rows={2} /></Form.Item>
          <Row gutter={8}>
            <Col span={8}><Form.Item name="pan" label="PAN"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="aadhaar" label="Aadhaar No."><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="mobile" label="Mobile"><Input /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      <Modal title={editingStudio ? 'Edit Studio' : 'Add Studio'} open={studioModal} onOk={saveStudio} onCancel={() => setStudioModal(false)} okText="Save">
        <Form form={studioForm} layout="vertical">
          <Form.Item name="name" label="Studio Name (jaisa song me select karte ho, e.g. Mahakal Studio Kanota)" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="rep_name" label="Owner / Representative Name"><Input /></Form.Item>
          <Form.Item name="rep_father" label="Owner's Father Name (S/O)"><Input /></Form.Item>
          <Form.Item name="address" label="Full Address"><Input.TextArea rows={2} /></Form.Item>
          <Row gutter={8}>
            <Col span={8}><Form.Item name="pan" label="PAN"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="aadhaar" label="Aadhaar No."><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="mobile" label="Mobile"><Input /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
